<template>
  <div class="timeline-tree-container">
    <div v-if="song != null">
      <!-- Song Info -->

      <v-header
        :song="song"
        :artist="artist"
      />

      <!-- Tree -->

      <div class="tree">
        <div class="title">
          Complete tree of adaptations
        </div>

        <div class="languages-container">
          <div class="languages">
            <div
              v-for="language in tree.languages"
              :key="language"
              class="language"
            >
              <div
                class="circle"
                :style="{backgroundColor: tree.color(language)}"
              />
              <span
                class="text"
                :style="{color: tree.color(language)}"
              >{{ $t('language.' + language.capitalize()) }}</span>
            </div>
          </div>
        </div>

        <div class="svg-container">
          <svg
            v-if="tree != null"
            :width="tree.width + tree.margin.left + tree.margin.right"
            :height="tree.height + tree.margin.top + tree.margin.bottom"
          >
            <g :transform="`translate(${tree.margin.left},${tree.margin.top})`">
              <g class="links">
                <g
                  v-for="(link, i) in tree.links"
                  :key="i"
                  class="link"
                >
                  <path
                    :d="tree.diagonal({source: link.source, target: link.target})"
                    :stroke-width="pathStrokeWidth(link)"
                    :stroke="pathStroke(link)"
                    :opacity="pathOpacity(link)"
                    fill="transparent"
                  />
                </g>
              </g>

              <g class="nodes">
                <g
                  v-for="(node, i) in tree.nodes"
                  :key="i"
                >
                  <g class="node">
                    <circle
                      v-if="node.data.id == song.id"
                      class="active-node"
                      :cx="node.x"
                      :cy="node.y"
                      :style="{transformOrigin: `${node.x}px ${node.y}px`}"
                      fill="transparent"
                      stroke-width="5"
                      opacity="0.4"
                      :stroke="dotColor(song)"
                      r="4"
                    />
                    <circle
                      class="dot"
                      r="4"
                      :cx="node.x"
                      :cy="node.y"
                      :fill="dotColor(node.data)"
                      :stroke="dotStroke(node.data)"
                      :stroke-width="dotStrokeWidth(node.data)"
                      @mouseenter="onMouseover(node.data)"
                      @mouseleave="onMouseover(null)"
                      @click="onSongClick(node.data)"
                    />
                    <g class="covers">
                      <circle
                        v-for="(cover, j) in coverSongs(node.data)"
                        :key="cover.cover_artist_id"
                        class="cover"
                        r="2.5"
                        :cx="coverX(node, j)"
                        :cy="coverY(node, j)"
                        fill="#ffffff"
                        :stroke="dotStroke(cover, true)"
                        :stroke-width="dotStrokeWidth(cover)"
                        @mouseenter="onMouseover(cover)"
                        @mouseleave="onMouseover(null)"
                        @click="onSongClick(node.data)"
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>
    <div
      v-else
      class="empty-container"
    >
      No song selected
    </div>
  </div>
</template>

<script>
import { isNumber } from "lodash";
import { Tree } from "./tree.js";
import { rgb } from "d3";
import Header from "./Header";
import { deepCopy } from "../../../services/helpers";

export default {
	components: {
		"v-header": Header
	},
	props: {
		song: {
			default: null
		},
		mouseover: {
			required: false,
			default: null
		},
		artistMouseover: {
			required: true
		},
		artist: {
			required: true,
			type: Object
		}
	},
	data() {
		return {
			tree: null,
			hideTuto: false
		};
	},
	computed: {
		hasWhiteCircle() {
			if (this.tree == null) {
				return false;
			}
			for (let i = 0; i < this.tree.nodes.length; i++) {
				if (!this.tree.nodes[i].data.level) {
					return true;
				}
			}
			return false;
		}
	},
	watch: {
		song() {
			this.render();
		}
	},
	created() {
		this.render();
	},
	methods: {
		render() {
			if (this.song != null) {
				this.tree = new Tree(this.song.id, this.$parent.timeline);
			}
		},
		dotCurrent (song) {
			return this.song.id == song.id && !song.cover;
		},
		dotActive(song) {
			if (this.mouseover == null) {
				return false;
			}
			if (song.cover) {
				return (
					this.mouseover.cover_artist_id == song.cover_artist_id &&
          this.mouseover.id == song.id
				);
			} else {
				return !this.mouseover.cover && this.mouseover.id == song.id;
			}
		},
		dotColor(song) {
			return this.tree.color(song.language);
		},
		dotStroke(song, cover = false) {
			if (this.dotActive(song)) {
				return "#000";
			}
			let darker = cover ? 0.4 : 2;
			return rgb(this.dotColor(song)).darker(darker);
		},
		dotStrokeWidth(song) {
			return this.dotActive(song) ? 2 : 1;
		},
		coverSongs(song) {
			let covers = [];
			song.cover_ids.forEach(cover_id => {
				let cover = deepCopy(song);
				cover.cover = true;
				cover.cover_artist_id = cover_id;
				covers.push(cover);
			});
			return covers;
		},
		coverLevel(song, i) {
			let total = song.data.cover_ids.length;
			let layers = [12, 18, 26, 1000];
			let baseRadius = 12;
			let radiusCoef = 5;

			let count = 0;
			let totalOnSubLayers = 0;
			let currentLayerIndex = 0;
			let done = false;
			for (let j = 0; j < layers.length && !done; j++) {
				count += layers[j];

				if (i + 1 <= count) {
					currentLayerIndex = j;
					done = true;
					break;
				}

				totalOnSubLayers = count;
			}

			let currentLayer = layers[currentLayerIndex];
			let radius = baseRadius + radiusCoef * currentLayerIndex;

			let remaining = total - totalOnSubLayers;
			let totalOnLayer = Math.min(currentLayer, remaining);
			let index = i - totalOnSubLayers;
			let angle = (2 * Math.PI * index) / totalOnLayer;

			return [
				song.x + radius * Math.cos(angle),
				song.y + radius * Math.sin(angle)
			];
		},
		coverX(song, i) {
			return this.coverLevel(song, i)[0];
		},
		coverY(song, i) {
			return this.coverLevel(song, i)[1];
		},
		pathRelated(link) {
			return (
				link.source.data.level !== undefined &&
        link.target.data.level !== undefined
			);
		},
		pathOpacity(link) {
			return 1;
		},
		pathStroke(link) {
			if (this.pathRelated(link)) {
				return "#000";
			}
			return "#f1f1f1";
		},
		pathStrokeWidth(link) {
			if (this.pathRelated(link)) {
				return 1.8;
			}
			return 1;
		},
		onMouseover(song) {
			this.$emit("onMouseover", song);
		},
		onSongClick(song) {
			this.$emit("onClick", song);
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.timeline-tree-container {
  height: 396px;
  position: relative;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.05);
  overflow-y: auto;

}

.tree {
  border-top: 1px #e1e1e1 solid;
  padding: 1rem 1rem;

  .title {
    font-size: 14px;
    margin-bottom: 5px;
  }
}

.svg-container {
  position: relative;
  display: block;
  overflow-y: auto;
  overflow-x: auto;
  text-align: center;
  width: 100%;

  svg {
    margin: 0 auto;
  }
}

.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.languages-container {
  width: 100%;
  overflow-x: scroll;

  &::-webkit-scrollbar {
    height: 0px; /* Remove scrollbar space */
    background: transparent; /* Optional: just make scrollbar invisible */
  }
  /* Optional: show position indicator in red */
  &::-webkit-scrollbar-thumb {
    background: #ff0000;
  }
}

.languages {
  display: flex;
  flex-wrap: nowrap;
  width: fit-content;
  margin-bottom: 10px;

  &.is-active {
    opacity: 1;
  }

  .text {
    font-size: 13px;
  }

  .language {
    display: flex;
    margin-right: 10px;
    align-items: center;
  }
  .circle {
    height: 9px;
    width: 9px;
    border-radius: 15px;
    margin-right: 4px;
  }
}

// Active circle animation

@keyframes pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}
.active-node {
  animation: pop 1s infinite;
}
</style>

